import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Spin, Tag, Table, Image, Row, Col } from '@/shared/ui/primitives';
import { useTranslation } from 'react-i18next';
import PageHeader from '@/shared/components/PageHeader';
import { Button, Space } from '@/shared/ui/primitives';
import { LinkOutlined } from '@/shared/ui/icons';
import { publicRoomAdUrl } from '@/shared/utils/publicLinks';
import { useRoomAdDetails } from '../hooks/useRoomAdDetails';
import { formatDateTime, formatAdPrice } from '@/shared/utils/formatters';
import { getAdImageOrDefault } from '@/shared/assets/defaultAdImages';
import PhoneActionLinks from '@/shared/components/PhoneActionLinks';
import AiAdWhatsAppUpdatesForAd from '@/features/ai-ads/components/AiAdWhatsAppUpdatesForAd';

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  PUBLISHED: 'success',
  ARCHIVED: 'warning',
  REJECTED: 'error',
};

export default function RoomAdDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useRoomAdDetails(id);

  if (isLoading) return <Spin style={{ display: 'block', margin: '80px auto' }} />;
  if (!data) return <Card><p>{t('roomAds.notFound')}</p></Card>;
  const hasImages = Array.isArray(data.images) && data.images.length > 0;
  const isArabic = i18n.language?.startsWith('ar');

  const roomDetailColumns = [
    { title: t('common.id'), dataIndex: 'id', key: 'id' },
    { title: t('roomAds.rent'), dataIndex: 'rent', key: 'rent', render: (v: any) => formatAdPrice(v, t) },
    { title: t('roomAds.deposit'), dataIndex: 'deposit', key: 'deposit', render: (v: any) => v ?? '—' },
    { title: t('roomAds.billType'), dataIndex: 'billType', key: 'billType', render: (v: any) => displayEnum(v, t) },
    { title: t('roomAds.rentType'), dataIndex: 'rentType', key: 'rentType', render: (v: any) => displayEnum(v, t) },
    { title: t('roomAds.roomType'), dataIndex: 'roomType', key: 'roomType', render: (v: any) => displayEnum(v, t) },
    { title: t('roomAds.furnishing'), dataIndex: 'propertyFurnishing', key: 'propertyFurnishing', render: (v: any) => displayEnum(v, t) },
  ];

  return (
    <div>
      <PageHeader back
        title={t('roomAds.detailsTitle')}
        subtitle={`#${data.id}: ${data.title || ''}`}
        action={
          <Space>
            <Button
              icon={<LinkOutlined />}
              data-testid="roomad-detail-public-link"
              onClick={() => window.open(publicRoomAdUrl(data.id), '_blank', 'noopener,noreferrer')}
            >
              {t('common.viewOnSite', { defaultValue: 'View on site' })}
            </Button>
            <Button type="primary" data-testid="roomad-detail-edit" onClick={() => navigate(`/room-ads/${id}/edit`)}>
              {t('common.edit')}
            </Button>
          </Space>
        }
      />

      <AiAdWhatsAppUpdatesForAd targetType="ROOM_AD" targetAdId={data.id} />

      {/* General */}
      <Card title={t('roomAds.general')} style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
          <Descriptions.Item label={t('common.id')}>{data.id}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.status')}>
            <Tag color={STATUS_COLORS[data.status] || 'default'}>{t(`enum.${data.status}`, { defaultValue: data.status })}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('roomAds.currentStep')}>{displayEnum(data.currentStep, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.advertiserType')}>{displayEnum(data.advertiserType, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.agentType')}>{displayEnum(data.realEstateAgentType, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.propertyType')}>{displayEnum(data.propertyType, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.bedrooms')}>{data.numberOfBedrooms ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.daysAvailable')}>{displayEnum(data.daysAvailableOption, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.minStay')}>{displayEnum(data.minimumStayOption, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.maxStay')}>{displayEnum(data.maximumStayOption, t)}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.featured')}>
            <Tag color={data.isFeatured ? 'purple' : 'default'}>{data.isFeatured ? t('common.yes') : t('common.no')}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('roomAds.bathrooms')}>{data.numberOfBathrooms ?? '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.availableFrom')}>{data.availableFrom || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.showPhone')}>{data.showPhoneNumber ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.showWhatsapp')}>{data.showWhatsappContact ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.showOwnerInfo')}>{data.showOwnerInfo ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.showInAppMessaging')}>{data.showInAppMessaging ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.phoneNumber')}>
            <PhoneActionLinks phonePrefix={data.phonePrefix} phoneNumber={data.phoneNumber} />
          </Descriptions.Item>
          <Descriptions.Item label={t('roomAds.hideLocation')}>{data.hideExactLocation ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.licenseNumber')}>{data.adLicenseNumber || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.deedNumber')}>{data.propertyDeedNumber || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.nationalId')}>{data.nationalID || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('roomAds.companyNumber')}>{data.companyUnifiedNumber || '—'}</Descriptions.Item>
        </Descriptions>
        {data.description && <p style={{ marginTop: 16 }}>{data.description}</p>}
        {data.rejectReason && <p style={{ marginTop: 8, color: 'var(--error)' }}>{t('roomAds.rejectReason')}: {data.rejectReason}</p>}
      </Card>

      {/* User & Location */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card title={t('roomAds.user')}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('roomAds.user')}>
                {data.userName ? `${data.userName} (#${data.userId})` : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title={t('roomAds.location')}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('roomAds.city')}>{localizedGeoName(data, 'city', isArabic)}</Descriptions.Item>
              <Descriptions.Item label={t('roomAds.district')}>{localizedGeoName(data, 'district', isArabic)}</Descriptions.Item>
              <Descriptions.Item label={t('roomAds.latLng')}>
                {data.latitude != null ? `${data.latitude}, ${data.longitude}` : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Room Details */}
      {data.roomDetails?.length > 0 && (
        <Card title={`${t('roomAds.roomDetails')} (${data.roomDetails.length})`} style={{ marginBottom: 16 }}>
          <Table
            rowKey="id"
            columns={roomDetailColumns}
            dataSource={data.roomDetails}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {/* Amenities */}
      {data.amenities?.length > 0 && (
        <Card title={`${t('roomAds.amenities')} (${data.amenities.length})`} style={{ marginBottom: 16 }}>
          {data.amenities.map((a: any) => (
            <Tag key={a.id} color="blue" style={{ marginBottom: 4 }}>{a.nameEn}</Tag>
          ))}
        </Card>
      )}

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <PreferenceCard title={t('roomAds.currentRoommates')} prefs={data.currentRoommates} />
        </Col>
        <Col xs={24} md={12}>
          <PreferenceCard title={t('roomAds.preferredRoommates')} prefs={data.preferredRoommates} />
        </Col>
      </Row>

      {/* Images */}
      <Card title={`${t('roomAds.images.sectionTitle')} (${hasImages ? data.images.length : 1})`} style={{ marginBottom: 16 }}>
        {hasImages ? (
          <Image.PreviewGroup>
            <Row gutter={[16, 16]}>
              {data.images.map((img: any) => (
                <Col key={img.id} xs={12} sm={8} md={6}>
                  <div style={{ position: 'relative' }}>
                    <Image
                      src={img.imageUrl}
                      alt={t('roomAds.images.imageAlt', { position: img.position })}
                      width="100%"
                      height={128}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                    />
                    {img.isPrimary && (
                      <Tag color="blue" style={{ position: 'absolute', top: 4, left: 4 }}>{t('roomAds.primary')}</Tag>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Image.PreviewGroup>
        ) : (
          <Image
            src={getAdImageOrDefault(data)}
            alt={t('roomAds.images.defaultAlt')}
            width={192}
            height={128}
            style={{ objectFit: 'cover', borderRadius: 8 }}
          />
        )}
      </Card>

      {/* Timestamps */}
      <Card>
        <Descriptions column={{ xs: 1, sm: 2 }} size="small">
          <Descriptions.Item label={t('common.createdAt')}>{formatDateTime(data.createdAt)}</Descriptions.Item>
          <Descriptions.Item label={t('common.updatedAt')}>{formatDateTime(data.updatedAt)}</Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
}

function PreferenceCard({ title, prefs }: { title: string; prefs: any }) {
  const { t } = useTranslation();
  return (
    <Card title={title}>
      <Descriptions column={1} size="small">
        <Descriptions.Item label={t('roommatePreferences.maleCount')}>{prefs?.maleRoommatesCount ?? '—'}</Descriptions.Item>
        <Descriptions.Item label={t('roommatePreferences.femaleCount')}>{prefs?.femaleRoommatesCount ?? '—'}</Descriptions.Item>
        <Descriptions.Item label={t('roommatePreferences.ageRange')}>{prefs ? `${prefs.minAge ?? '—'} - ${prefs.maxAge ?? '—'}` : '—'}</Descriptions.Item>
        <Descriptions.Item label={t('roommatePreferences.nationality')}>{prefs?.nationalityName || '—'}</Descriptions.Item>
        <Descriptions.Item label={t('roommatePreferences.gender')}>{displayEnum(prefs?.genderPreference, t)}</Descriptions.Item>
        <Descriptions.Item label={t('roommatePreferences.smoking')}>{displayEnum(prefs?.smokingPreference, t)}</Descriptions.Item>
        <Descriptions.Item label={t('roommatePreferences.occupation')}>{displayEnum(prefs?.occupationType, t)}</Descriptions.Item>
        <Descriptions.Item label={t('roommatePreferences.language')}>{displayEnum(prefs?.languagePreference, t)}</Descriptions.Item>
      </Descriptions>
    </Card>
  );
}

function displayEnum(value: string | null | undefined, t: (key: string, options?: any) => string) {
  return value ? t(`enum.${value}`, { defaultValue: value }) : '—';
}

function localizedGeoName(record: any, type: 'city' | 'district', isArabic: boolean) {
  const ar = record?.[`${type}NameAr`];
  const en = record?.[`${type}NameEn`];
  return (isArabic ? ar || en : en || ar) || '—';
}
